import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  motion } from 'framer-motion';
import { 
  LayoutTemplate, Search, Plus, Edit, Trash2, RefreshCcw, CheckCircle, AlertTriangle, XCircle, 
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, LayoutGrid, Layers, Users, Clock, TrendingUp, FileText,
  List, LayoutList, Shield, Settings, Briefcase, Coffee
} from 'lucide-react';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import localApiAdapter from '../../core/services/localApiAdapter';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import {
  usePointageModalBodyLock,
  getPointageModalFormClasses,
  buildPointageColumnVisibilityState,
  buildPointageQawaemColumnDefs,
  isPointageColumnVisible,
  createPointageHandleToggleColumn,
  pointageTableColSpan,
  buildPointageBulkDeleteFilterAction,
  POINTAGE_ACTIONS_TH_CLASS,
  PointageCrudNotificationToast,
  useAppNotificationToast,
  notifyBulkDeleted,
  notifyCreated,
  notifyDeleted,
  notifyError,
  notifyUpdated,
  sortRecordsNewestFirst,
  buildPointageFormTabs,
  pointageFormTabVisible,
} from './pointageListPageUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import PointageFormModalShell, { POINTAGE_FORM_GRID_CLASS } from './components/PointageFormModalShell';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import CustomTimeInput from '../../shared/components/custom/CustomTimeInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { useTranslation } from 'react-i18next';
import { formatPointageDate, formatPointageMonthYear, getBrandDatePickerLocale, PROFILE_TYPE_VALUES, FLEXIBILITY_TYPE_VALUES, normalizeProfileType, normalizeFlexibilityType, SAUDI_DEFAULT_TIMETRACKING_CONFIG } from './pointageHelpers';

registerLocale('ar', ar);

const ProfilsHorairesPage = () => {
  const { t } = useTranslation();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const p = (key, opts) => t(`modules.pointage.pages.profilsHorairesPage.${key}`, opts);

  const getStatusLabel = (status) => {
    if (status === 'Actif') return p('status_active');
    if (status === 'Inactif') return p('status_inactive');
    return p('em_dash');
  };

  const getProfileTypeLabel = (value) => {
    const normalized = normalizeProfileType(value);
    const map = {
      [PROFILE_TYPE_VALUES.standard]: p('type_standard'),
      [PROFILE_TYPE_VALUES.custom]: p('type_custom'),
      [PROFILE_TYPE_VALUES.fullTime]: p('type_full_time'),
      [PROFILE_TYPE_VALUES.partTime]: p('type_part_time'),
      [PROFILE_TYPE_VALUES.executive]: p('type_executive'),
    };
    return map[normalized] || value || p('em_dash');
  };

  const getFlexibilityLabel = (value) => {
    const normalized = normalizeFlexibilityType(value);
    const map = {
      [FLEXIBILITY_TYPE_VALUES.fixed]: p('flex_fixed'),
      [FLEXIBILITY_TYPE_VALUES.variable]: p('flex_variable'),
      [FLEXIBILITY_TYPE_VALUES.remote]: p('flex_remote'),
      [FLEXIBILITY_TYPE_VALUES.hybrid]: p('flex_hybrid'),
    };
    return map[normalized] || value || p('em_dash');
  };

  const profileTypeOptions = [
    { value: PROFILE_TYPE_VALUES.standard, label: p('type_standard'), isDefault: true },
    { value: PROFILE_TYPE_VALUES.custom, label: p('type_custom'), isDefault: true },
    { value: PROFILE_TYPE_VALUES.fullTime, label: p('type_full_time'), isDefault: true },
    { value: PROFILE_TYPE_VALUES.partTime, label: p('type_part_time'), isDefault: true },
    { value: PROFILE_TYPE_VALUES.executive, label: p('type_executive'), isDefault: true },
  ];

  const [searchTerm, setSearchTerm] = useState('');
  const [profils, setProfils] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);

  const cfgTheme = useModuleConfigTheme();
  const modalForm = getPointageModalFormClasses(cfgTheme.light);
  usePointageModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('general');

  const PROFIL_FORM_FIELD_COUNT = 14;

  const profilFormTabs = useMemo(() => buildPointageFormTabs({
    two: [
      { id: 'general', label: p('section_profile_info'), icon: LayoutTemplate, iconClass: 'text-emerald-400' },
      { id: 'details', label: p('section_application'), icon: Users, iconClass: 'text-pink-400' },
    ],
  }, PROFIL_FORM_FIELD_COUNT), [p]);
  
  const [formData, setFormData] = useState({
    profile_code: '',
    profile_name: '',
    profile_type: '',
    weekly_hours: '',
    daily_hours: '',
    flexibility_type: '',
    break_duration_minutes: '',
    core_hours_start: '',
    core_hours_end: '',
    assigned_employees_count: '',
    status: '',
    effective_date: '',
    description: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_profiles');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const dateRangeRef = useRef(null);
  
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { loadProfils(); }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProfils = async () => {
    try {
      const response = await localApiAdapter.get('/pointage/profils-horaires');
      const rows = response?.data || response || [];
      setProfils(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading profils horaires:', error);
      setProfils([]);
    }
  };

  const stats = {
    total: profils.length,
    actifs: profils.filter(p => p.status === 'Actif').length,
    standard: profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.standard).length,
    personnalises: profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.custom).length,
    employesAssignes: profils.reduce((sum, p) => sum + (parseInt(p.assigned_employees_count) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_profiles', name: p('tab_general'), icon: LayoutGrid },
    { id: 'by_type', name: p('tab_by_type'), icon: Shield },
    { id: 'by_status', name: p('tab_by_status'), icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: PROFILE_TYPE_VALUES.standard, name: p('type_standard'), icon: Clock },
    { id: PROFILE_TYPE_VALUES.custom, name: p('type_custom'), icon: Settings },
    { id: PROFILE_TYPE_VALUES.fullTime, name: p('type_full_time'), icon: Briefcase },
    { id: PROFILE_TYPE_VALUES.partTime, name: p('type_part_time'), icon: Coffee }
  ];

  const getTypeDistribution = () => {
    const standard = profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.standard).length;
    const personnalise = profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.custom).length;
    const tempsPlein = profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.fullTime).length;
    const tempsPartiel = profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.partTime).length;
    const cadre = profils.filter(pr => normalizeProfileType(pr.profile_type) === PROFILE_TYPE_VALUES.executive).length;
    
    return [
      { filterKey: PROFILE_TYPE_VALUES.standard, name: p('type_standard'), count: standard, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
      { filterKey: PROFILE_TYPE_VALUES.custom, name: p('type_custom'), count: personnalise, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
      { filterKey: PROFILE_TYPE_VALUES.fullTime, name: p('type_full_time'), count: tempsPlein, color: "bg-gradient-to-r from-green-500 to-green-600" },
      { filterKey: PROFILE_TYPE_VALUES.partTime, name: p('type_part_time'), count: tempsPartiel, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
      { filterKey: PROFILE_TYPE_VALUES.executive, name: p('type_executive'), count: cadre, color: "bg-gradient-to-r from-orange-500 to-orange-600" }
    ];
  };

  const profilDistribution = {
    all_profiles: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.actifs, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'inactive', name: p('dist_inactive'), count: profils.filter(pr => pr.status === 'Inactif').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" },
        { filterKey: PROFILE_TYPE_VALUES.standard, name: p('type_standard'), count: stats.standard, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterKey: PROFILE_TYPE_VALUES.custom, name: p('dist_custom_plural'), count: stats.personnalises, color: "bg-gradient-to-r from-purple-500 to-purple-600" }
      ]
    },
    by_type: {
      title: p('dist_by_type_title'),
      data: getTypeDistribution()
    },
    by_status: {
      title: p('dist_by_status_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.actifs, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'inactive', name: p('dist_inactive'), count: profils.filter(pr => pr.status === 'Inactif').length, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
      ]
    }
  };

  const distributionInfo = profilDistribution[mainViewMode] || profilDistribution.all_profiles;

  const baseFilteredProfils = useMemo(() => profils.filter((profil) => {
    if (statusFilter !== 'all' && profil.status !== statusFilter) return false;

    if (dateRange.start || dateRange.end) {
      const effectiveDate = new Date(profil.effective_date);

      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (effectiveDate < startOfDay) return false;
      }

      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (effectiveDate > endOfDay) return false;
      }
    }

    if (searchTerm && !profil.profile_code?.toLowerCase().includes(searchTerm.toLowerCase()) && !profil.profile_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (mainViewMode === 'all_profiles') {
      if (quickFilter !== 'all' && normalizeProfileType(profil.profile_type) !== quickFilter) return false;
    }
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_profiles':
        case 'by_status':
          if (activeDistributionFilter === 'active' && profil.status !== 'Actif') return false;
          if (activeDistributionFilter === 'inactive' && profil.status !== 'Inactif') return false;
          if (activeDistributionFilter === PROFILE_TYPE_VALUES.standard && normalizeProfileType(profil.profile_type) !== PROFILE_TYPE_VALUES.standard) return false;
          if (activeDistributionFilter === PROFILE_TYPE_VALUES.custom && normalizeProfileType(profil.profile_type) !== PROFILE_TYPE_VALUES.custom) return false;
          break;
        case 'by_type':
          if (normalizeProfileType(profil.profile_type) !== activeDistributionFilter) return false;
          break;
      }
    }
    return true;
  }), [profils, statusFilter, dateRange, searchTerm, mainViewMode, quickFilter, activeDistributionFilter]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('profile_code', (pr) => pr.profile_code || ''),
      textColumnRule('profile_name', (pr) => pr.profile_name || ''),
      textColumnRule('profile_type', (pr) => getProfileTypeLabel(pr.profile_type)),
      textColumnRule('weekly_hours', (pr) => String(pr.weekly_hours ?? '')),
      textColumnRule('flexibility_type', (pr) => getFlexibilityLabel(pr.flexibility_type)),
      textColumnRule('assigned_employees_count', (pr) => String(pr.assigned_employees_count ?? '')),
      exactColumnRule('status', (pr) => pr.status || ''),
    ],
    [p],
  );

  const filteredProfils = useMemo(
    () => applyColumnFilters(baseFilteredProfils, colFilters, columnFilterRules),
    [baseFilteredProfils, colFilters, columnFilterRules],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (editingRecord) {
        await localApiAdapter.put(`/pointage/profils-horaires/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('title'));
      } else {
        await localApiAdapter.post('/pointage/profils-horaires', submitData);
        notifyCreated(showNotify, p('title'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error')); return;
    } finally {
      resetForm();
      loadProfils();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/pointage/profils-horaires/${id}`);
        notifyDeleted(showNotify, p('title'));
        loadProfils();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/pointage/profils-horaires/${id}`, { status: newStatus });
      loadProfils();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/pointage/profils-horaires/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('title'));
        setSelectedRecords([]);
        loadProfils();
      } catch (error) {
        console.error('Error:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      profile_code: '',
      profile_name: '',
      profile_type: '',
      weekly_hours: '',
      daily_hours: '',
      flexibility_type: '',
      break_duration_minutes: '',
      core_hours_start: '',
      core_hours_end: '',
      assigned_employees_count: '',
      status: '',
      effective_date: '',
      description: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  const {
    selectedRecords,
    setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
  } = useQawaemFilteredRowSelection(filteredProfils);


  const POINTAGE_TABLE_COLUMNS = [
    { key: 'profile_code', labelKey: 'col_code' },
    { key: 'profile_name', labelKey: 'col_profile_name' },
    { key: 'profile_type', labelKey: 'type' },
    { key: 'weekly_hours', labelKey: 'col_weekly_hours' },
    { key: 'flexibility_type', labelKey: 'col_flexibility' },
    { key: 'assigned_employees_count', labelKey: 'col_employees' },
    { key: 'status', labelKey: 'col_status' },
    { key: 'actions', labelKey: 'actions', required: true },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildPointageColumnVisibilityState(POINTAGE_TABLE_COLUMNS),
  );
  const qawaemColumnDefs = useMemo(
    () => buildPointageQawaemColumnDefs(POINTAGE_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createPointageHandleToggleColumn(setColumnVisibility, POINTAGE_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isPointageColumnVisible(columnVisibility, key);
  const tableColSpan = pointageTableColSpan(columnVisibility);

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <LayoutTemplate className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadProfils} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          {buildPointageBulkDeleteFilterAction({ count: selectedRecords.length, onDelete: handleBulkDelete, label: `${t('common.delete')} (${selectedRecords.length})`, title: t('common.delete', { defaultValue: 'حذف' }) })}
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_profile')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: LayoutTemplate, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_active'), value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_standard'), value: stats.standard, icon: Clock, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_custom'), value: stats.personnalises, icon: Settings, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_assigned'), value: stats.employesAssignes, icon: Users, color: 'from-pink-500 to-pink-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_profiles' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterKey || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('pct_of_total', { pct: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader allRowsSelected={allRowsSelected} someRowsSelected={someRowsSelected} onChange={handleSelectAll} selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })} deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء تحديد الكل' })} />
                  {isColVisible('profile_code') && (
                    <TableColumnHeaderFilter columnKey="profile_code" label={p('col_code')} value={colFilters.profile_code ?? ''} onChange={(v) => setColFilter('profile_code', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('profile_name') && (
                    <TableColumnHeaderFilter columnKey="profile_name" label={p('col_profile_name')} value={colFilters.profile_name ?? ''} onChange={(v) => setColFilter('profile_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('profile_type') && (
                    <TableColumnHeaderFilter columnKey="profile_type" label={p('type')} value={colFilters.profile_type ?? ''} onChange={(v) => setColFilter('profile_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('weekly_hours') && (
                    <TableColumnHeaderFilter columnKey="weekly_hours" label={p('col_weekly_hours')} value={colFilters.weekly_hours ?? ''} onChange={(v) => setColFilter('weekly_hours', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('flexibility_type') && (
                    <TableColumnHeaderFilter columnKey="flexibility_type" label={p('col_flexibility')} value={colFilters.flexibility_type ?? ''} onChange={(v) => setColFilter('flexibility_type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  )}
                  {isColVisible('assigned_employees_count') && (
                    <TableColumnHeaderFilter columnKey="assigned_employees_count" label={p('col_employees')} value={colFilters.assigned_employees_count ?? ''} onChange={(v) => setColFilter('assigned_employees_count', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  {isColVisible('status') && (
                    <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusColumnFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white" />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredProfils.length > 0 ? (
                  filteredProfils.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell checked={selectedRecords.includes(record.id)} onChange={() => handleSelectRecord(record.id)} />
                      {isColVisible('profile_code') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.profile_code}</span></td>
                      )}
                      {isColVisible('profile_name') && (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.profile_name}</span></td>
                      )}
                      {isColVisible('profile_type') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getProfileTypeLabel(record.profile_type)}</span></td>
                      )}
                      {isColVisible('weekly_hours') && (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.weekly_hours || 0}{p('hours_suffix')}</span></td>
                      )}
                      {isColVisible('flexibility_type') && (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{getFlexibilityLabel(record.flexibility_type)}</span></td>
                      )}
                      {isColVisible('assigned_employees_count') && (
                        <td className="px-6 py-4 text-center"><span className="text-white font-bold text-lg">{record.assigned_employees_count || 0}</span></td>
                      )}
                      {isColVisible('status') && (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={p('click_change_status', { defaultValue: t('common.change_status') })}
                            />
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex justify-center">
                          <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><LayoutTemplate size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredProfils.length })}</span>
                      <span>{p('footer_assigned')} <span className="text-white font-semibold">{filteredProfils.reduce((sum, pr) => sum + (parseInt(pr.assigned_employees_count) || 0), 0)}</span></span>
                      <span>{p('footer_avg_hours')} <span className="text-white font-semibold">{filteredProfils.length > 0 ? (filteredProfils.reduce((sum, pr) => sum + (parseFloat(pr.weekly_hours) || 0), 0) / filteredProfils.length).toFixed(1) : 0}{p('hours_suffix')}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredProfils.length > 0 ? (
              filteredProfils.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><LayoutTemplate size={20} className="text-emerald-400" />{record.profile_name}</h3>
                        <div className="flex">{getStatusBadge(record.status)}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_profile')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}</span> {record.profile_code}</div>
                            <div><span className="font-medium">{p('label_name')}</span> {record.profile_name}</div>
                            <div><span className="font-medium">{p('label_type')}</span> {getProfileTypeLabel(record.profile_type)}</div>
                            <div><span className="font-medium">{p('label_status')}</span> {getStatusLabel(record.status)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_schedule')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_weekly_hours')}</span> <span className="text-white font-bold">{record.weekly_hours || 0}{p('hours_suffix')}</span></div>
                            <div><span className="font-medium">{p('label_daily_hours')}</span> {record.daily_hours || 0}{p('hours_suffix')}</div>
                            <div><span className="font-medium">{p('label_core_hours')}</span> {record.core_hours_start || p('em_dash')} - {record.core_hours_end || p('em_dash')}</div>
                            <div><span className="font-medium">{p('label_break')}</span> {record.break_duration_minutes || 0} min</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_flexibility')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_flex_type')}</span> {getFlexibilityLabel(record.flexibility_type)}</div>
                            <div><span className="font-medium">{p('label_effective_date')}</span> {record.effective_date ? formatPointageDate(record.effective_date) : p('em_dash')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_section_assignment')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_assigned')}</span> <span className="text-white font-bold text-lg">{record.assigned_employees_count || 0}</span></div>
                            {record.notes && <div className="pt-2 border-t border-white/20"><span className="font-medium">{p('label_notes')}</span> <p className="text-white/60 text-xs mt-1">{record.notes}</p></div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4 shrink-0">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><LayoutTemplate size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {showViewModal && viewingRecord && (
        <QawaemErpViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={p('view_modal_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="profils-horaires-view-modal"
        >
          <div className="space-y-4">
                <div className={modalForm.viewCard}>
                  <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><LayoutTemplate size={18} className="text-emerald-400" />{p('view_section_profile')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className={modalForm.viewLabel}>{p('view_profile_code')}</p><p className={modalForm.viewValue}>{viewingRecord.profile_code}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('view_profile_name')}</p><p className={modalForm.viewValue}>{viewingRecord.profile_name}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('type')}</p><p className={modalForm.viewValue}>{getProfileTypeLabel(viewingRecord.profile_type)}</p></div>
                    <div><p className={modalForm.viewLabel}>{p('status')}</p>{getStatusBadge(viewingRecord.status)}</div>
                    <div><p className={modalForm.viewLabel}>{p('view_effective_date')}</p><p className={modalForm.viewValue}>{viewingRecord.effective_date ? formatPointageDate(viewingRecord.effective_date) : p('em_dash')}</p></div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_schedule')}</h3>
                    <div className="space-y-3">
                      <div><p className={modalForm.viewLabel}>{p('view_weekly_hours')}</p><p className={modalForm.viewValue}>{viewingRecord.weekly_hours || 0}{p('hours_suffix')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_daily_hours')}</p><p className={modalForm.viewValue}>{viewingRecord.daily_hours || 0}{p('hours_suffix')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_core_hours')}</p><p className={modalForm.viewValue}>{viewingRecord.core_hours_start || p('em_dash')} - {viewingRecord.core_hours_end || p('em_dash')}</p></div>
                      <div><p className={modalForm.viewLabel}>{p('view_break_duration')}</p><p className={modalForm.viewValue}>{viewingRecord.break_duration_minutes || 0} {p('minutes')}</p></div>
                    </div>
                  </div>
                  <div className={modalForm.viewCard}>
                    <h3 className={`${modalForm.viewHeading} flex items-center gap-2`}><Settings size={18} className="text-blue-400" />{p('view_section_flex')}</h3>
                    <div className="space-y-4">
                      <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
                        <p className={modalForm.viewLabel}>{p('view_flex_type')}</p>
                        <p className="text-white font-bold text-xl">{getFlexibilityLabel(viewingRecord.flexibility_type)}</p>
                      </div>
                      <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/20">
                        <p className={modalForm.viewLabel}>{p('view_assigned')}</p>
                        <p className="text-emerald-400 font-bold text-2xl">{viewingRecord.assigned_employees_count || 0}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {(viewingRecord.description || viewingRecord.notes) && (
                  <div className={modalForm.viewCard}>
                    <h3 className={modalForm.viewHeading}>{p('view_section_desc')}</h3>
                    {viewingRecord.description && (
                      <div className="mb-3">
                        <p className={modalForm.viewLabel}>{p('view_description')}</p>
                        <p className="text-white/80 text-sm">{viewingRecord.description}</p>
                      </div>
                    )}
                    {viewingRecord.notes && (
                      <div>
                        <p className={modalForm.viewLabel}>{p('field_notes')}</p>
                        <p className="text-white/80 text-sm">{viewingRecord.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
        </QawaemErpViewModal>
      )}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_new')}
          icon={Clock}
          onSubmit
          formId="profil-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          maxWidth="1200px"
          testId="profils-horaires-crud-modal"
        >
          <PointageFormModalShell
            isLight={cfgTheme.light}
            tabs={profilFormTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            formId="profil-form"
            onSubmit={handleSubmit}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
          >
            {pointageFormTabVisible(profilFormTabs, formTab, 'general') && (
              <div className="space-y-4">
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_profile_code')} />}</label><input type="text" required value={formData.profile_code} onChange={(e) => setFormData({ ...formData, profile_code: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_profile_code')} /></div>
                <div><label className={modalForm.label}>{<RequiredLabelText text={p('field_profile_name')} />}</label><input type="text" required value={formData.profile_name} onChange={(e) => setFormData({ ...formData, profile_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_profile_name')} /></div>
                <div><label className={modalForm.label}>{p('field_profile_type')}</label><CustomSelect value={formData.profile_type} onChange={(value) => setFormData({ ...formData, profile_type: value })} options={profileTypeOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_select_or_create')} /></div>
                <div><label className={modalForm.label}>{p('field_status')}</label><CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions} searchable={true} creatable={true} autoSelect={false} /></div>
                <div><label className={modalForm.label}>{p('field_effective_date')}</label><DatePicker selected={formData.effective_date ? new Date(formData.effective_date) : null} onChange={(date) => setFormData({ ...formData, effective_date: date ? date.toISOString().split('T')[0] : '' })} dateFormat="dd/MM/yyyy" placeholderText={p('select')} locale={getBrandDatePickerLocale()} className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker" calendarClassName="gps-calendar" wrapperClassName="w-full" /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_description')}</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_description')} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_weekly_hours')}</label><CustomNumberInput step={0.5} value={formData.weekly_hours || ''} onChange={(value) => setFormData({ ...formData, weekly_hours: value })} placeholder={String(SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxWeeklyHours)} /></div>
                <div><label className={modalForm.label}>{p('field_daily_hours')}</label><CustomNumberInput step={0.5} value={formData.daily_hours || ''} onChange={(value) => setFormData({ ...formData, daily_hours: value })} placeholder={String(SAUDI_DEFAULT_TIMETRACKING_CONFIG.maxDailyHours)} /></div>
                <div><label className={modalForm.label}>{p('field_core_start')}</label><CustomTimeInput value={formData.core_hours_start} onChange={(value) => setFormData({ ...formData, core_hours_start: value })} placeholder={SAUDI_DEFAULT_TIMETRACKING_CONFIG.workDayStart} /></div>
                <div><label className={modalForm.label}>{p('field_core_end')}</label><CustomTimeInput value={formData.core_hours_end} onChange={(value) => setFormData({ ...formData, core_hours_end: value })} placeholder={SAUDI_DEFAULT_TIMETRACKING_CONFIG.workDayEnd} /></div>
              </div>
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_flex_type')}</label><CustomSelect value={formData.flexibility_type} onChange={(value) => setFormData({ ...formData, flexibility_type: value })} options={flexibilityTypeOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('ph_select_or_create')} /></div>
                <div><label className={modalForm.label}>{p('field_break_duration')}</label><CustomNumberInput step={5} value={formData.break_duration_minutes || ''} onChange={(value) => setFormData({ ...formData, break_duration_minutes: value })} placeholder="60" /></div>
              </div>
              </div>
            )}
            {pointageFormTabVisible(profilFormTabs, formTab, 'details') && (
              <div className={POINTAGE_FORM_GRID_CLASS}>
                <div><label className={modalForm.label}>{p('field_assigned_count')}</label><CustomNumberInput step={1} value={formData.assigned_employees_count || ''} onChange={(value) => setFormData({ ...formData, assigned_employees_count: value })} placeholder="0" /></div>
                <div className="md:col-span-3"><label className={modalForm.label}>{p('field_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('ph_notes')} /></div>
              </div>
            )}
          </PointageFormModalShell>
        </QawaemErpCrudModal>
      )}
      <PointageCrudNotificationToast toast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default ProfilsHorairesPage;


